import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  LifeBuoy,
  ArrowLeft,
  Calendar,
  MapPin,
  FileText,
  Loader2,
  Download,
  AlertCircle,
  Eye,
  MessageSquare,
  CheckCircle2,
  Image as ImageIcon,
} from "lucide-react";
import api from "../lib/api";
import { uploadUrl } from "../lib/uploads";
import useServerPdf from "../lib/useServerPdf";
import ReadAloudButton from "../components/ReadAloudButton";
import ExpandableText from "../components/ExpandableText";
import { useAuth } from "../context/AuthContext";
import MediaLightbox from "../components/MediaLightbox";
import { toast } from "../components/ui/sonner";

/**
 * /portal/tickets/:tid — Wave 26.4.
 *
 * Full-page customer-facing ticket view. Mirrors the JC equivalent
 * (`PortalJobCardDetail.jsx`) — same header / preview banner / fact
 * grid / section layout / TTS read-aloud + ExpandableText. Always
 * fetches from `GET /api/portal/tickets/:tid` which strips every
 * internal field (resolution_notes, costs, debrief, internal notes,
 * assigned_to_id, support_type, external_ref, internal attachments).
 *
 * Routing:
 *   /portal/tickets/:tid                  — real client (role=client)
 *   ?customer_id=<cid> query param        — staff in preview mode
 */
export default function PortalTicketDetail() {
  const { tid } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const previewCustomerId = search.get("customer_id");
  const isStaffPreview = !!user && user.role !== "client";

  const [ticket, setTicket] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const shortId = ticket?.short_id || ticket?.id?.slice(0, 8) || "ticket";
  const { download: downloadPdf, busy: downloadingPdf } = useServerPdf({
    endpoint: tid ? `/tickets/${tid}/pdf?as_client=1` : null,
    filename: `Ticket_${shortId}.pdf`,
    // [Iter 290zp.32] Portal must never silently fall back to
    // browser web-print — see PortalJobCardDetail note.
    fallbackPrint: false,
  });

  useEffect(() => {
    if (!tid) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    const params = previewCustomerId ? { customer_id: previewCustomerId } : {};
    (async () => {
      try {
        const { data } = await api.get(`/portal/tickets/${tid}`, { params });
        if (cancelled) return;
        setTicket(data);
        if (data.customer_id) {
          api.get(`/customers/${data.customer_id}`)
            .then((r) => !cancelled && setCustomer(r.data))
            .catch(() => {});
        }
        if (data.site_id) {
          api.get(`/sites`)
            .then((r) => {
              if (cancelled) return;
              setSite((r.data || []).find((s) => s.id === data.site_id));
            })
            .catch(() => {});
        }
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) setErr("This support ticket is no longer available.");
        else if (code === 403) setErr("You don't have access to this ticket.");
        else setErr("Could not load this ticket. Try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tid, previewCustomerId]);

  const attachmentSrc = (a) => {
    const url = typeof a === "string" ? a : a?.url || a?.path;
    if (!url) return "";
    // [Iter 327] `uploadUrl()` is now smart — accepts bare id, path,
    // or full URL (fresh OR with stale baked-in `?auth=`) and always
    // emits a fresh live URL. So we no longer need the `.startsWith`
    // guard here — just delegate.
    return uploadUrl(url) || url;
  };

  const visibleAttachments = (ticket?.attachments || []).filter(Boolean);

  // [Iter 347 — 2026-02-26] Split attachments into image vs. document.
  // Images render as a tap-to-view thumbnail grid backed by
  // `MediaLightbox` (close / prev / next / download / share) so
  // customers can browse ticket photos with the same UX as their JC
  // photos. Documents keep the existing blob-download row treatment.
  const _isImage = (a) => {
    const ct = a?.content_type || a?.contentType || "";
    if (ct.startsWith("image/")) return true;
    const name = a?.filename || a?.name || a?.label || a?.file_name || "";
    return /\.(png|jpe?g|webp|gif|heic|heif|bmp)(\?|$)/i.test(name);
  };
  const imageAttachments = visibleAttachments.filter(_isImage);
  const docAttachments = visibleAttachments.filter((a) => !_isImage(a));
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const lightboxItems = imageAttachments.map((a, i) => ({
    src: attachmentSrc(a),
    kind: "image",
    caption: a.label || a.filename || a.name || `Attachment ${i + 1}`,
    filename: a.filename || a.name || a.label || `attachment_${i + 1}`,
    contentType: a.content_type || a.contentType || "image/jpeg",
  }));

  // [Iter 345 — 2026-02-25] Auth-carrying blob download for portal
  // attachments. Same fix as PortalJobCardDetail — keeps the portal
  // shell intact and stops iOS PWA from booting back to CRM on exit.
  const downloadPortalAttachment = async (a) => {
    const url = attachmentSrc(a);
    if (!url) { toast.error("Attachment has no URL."); return; }
    try {
      let blob;
      const filename = a.label || a.filename || a.name || "attachment";
      if (url.startsWith("data:") || url.startsWith("http")) {
        const resp = await fetch(url, { credentials: "include" });
        blob = await resp.blob();
      } else {
        const path = url.startsWith("/api/") ? url.slice(4) : (url.startsWith("/") ? url : `/uploads/${url}`);
        const resp = await api.get(path, { responseType: "blob" });
        blob = resp.data;
      }
      const objUrl = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = objUrl;
      el.download = filename;
      el.rel = "noopener";
      document.body.appendChild(el);
      el.click();
      el.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 15_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Download failed.");
    }
  };
  const notes = ticket?.notes || [];

  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(isStaffPreview && previewCustomerId ? `/portal/preview/${previewCustomerId}` : "/portal");
  };

  return (
    <div className="min-h-screen bg-[#0a0b0e] text-white" data-testid="portal-ticket-detail">
      {isStaffPreview && (
        <div
          className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-xs py-2 px-4 flex items-center gap-2"
          data-testid="portal-ticket-preview-banner"
        >
          <Eye className="w-3.5 h-3.5" />
          <span>
            <strong>Preview mode</strong> — you're seeing what{" "}
            <strong>{customer?.name || "the customer"}</strong> sees. Internal
            notes, resolution notes, costs and staff-only attachments are hidden.
          </span>
        </div>
      )}

      <div className="border-b border-white/5 bg-[#0a0b0e]/80 backdrop-blur sticky top-0 z-20" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={back}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="portal-ticket-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">
              Support ticket · {customer?.name || "—"}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {ticket?.short_id && (
                <span className="font-mono text-xs text-[#1FB6C1]">#{ticket.short_id}</span>
              )}
              <h1 className="text-lg sm:text-xl font-display font-bold leading-tight truncate">
                {ticket?.subject || (loading ? "Loading…" : "—")}
              </h1>
              {ticket?.status && (
                <span
                  className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${
                    /resolved|closed/.test(ticket.status)
                      ? "border-emerald-500/40 text-emerald-300"
                      : ticket.status === "in_progress"
                      ? "border-amber-500/40 text-amber-300"
                      : "border-white/10 text-[#9aa3b5]"
                  }`}
                >
                  {ticket.status.replace("_", " ")}
                </span>
              )}
              {ticket?.subject && (
                <ReadAloudButton
                  text={ticket.subject}
                  size="xs"
                  testId="portal-ticket-title-tts"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading && (
          <div className="py-16 text-center text-sm text-[#9aa3b5]">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading ticket…
          </div>
        )}
        {err && !loading && (
          <div className="py-12 text-center text-sm text-rose-300 flex flex-col items-center gap-2">
            <AlertCircle className="w-6 h-6" /> {err}
          </div>
        )}
        {!loading && !err && ticket && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-3 gap-3">
              {ticket.created_at && (
                <FactCard
                  icon={Calendar}
                  label="Logged"
                  value={new Date(ticket.created_at).toLocaleString("en-GB", {
                    weekday: "short", day: "numeric", month: "short",
                    year: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                />
              )}
              <FactCard
                icon={MapPin}
                label="Site"
                value={site?.name || site?.address || "—"}
              />
              {ticket.priority && (
                <FactCard
                  icon={LifeBuoy}
                  label="Priority"
                  value={
                    <span
                      className={`text-xs uppercase tracking-wider ${
                        ticket.priority === "urgent" || ticket.priority === "high"
                          ? "text-rose-300"
                          : ticket.priority === "low"
                          ? "text-[#9aa3b5]"
                          : "text-amber-300"
                      }`}
                    >
                      {ticket.priority}
                    </span>
                  }
                />
              )}
            </div>

            {ticket.description && (
              <Section
                title="What you reported"
                action={<ReadAloudButton text={ticket.description} testId="portal-ticket-desc-tts" />}
              >
                <ExpandableText
                  text={ticket.description}
                  lines={6}
                  testId="portal-ticket-desc"
                />
              </Section>
            )}

            {notes.length > 0 && (
              <Section title={`Updates (${notes.length})`}>
                <ul className="space-y-2">
                  {notes.map((n, i) => (
                    <li
                      key={n.id || i}
                      className="rounded border border-white/5 bg-white/[0.02] p-3"
                      data-testid={`portal-ticket-note-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1 text-[10px] uppercase tracking-wider text-[#647087]">
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {n.by_user_name || "Team"}
                        </span>
                        <span>
                          {n.created_at &&
                            new Date(n.created_at).toLocaleString("en-GB")}
                        </span>
                      </div>
                      <ExpandableText
                        text={n.text}
                        lines={3}
                        testId={`portal-ticket-note-text-${i}`}
                      >
                        <ReadAloudButton
                          text={n.text}
                          size="xs"
                          testId={`portal-ticket-note-tts-${i}`}
                        />
                      </ExpandableText>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(imageAttachments.length > 0 || docAttachments.length > 0) && (
              <Section title={`Attachments (${visibleAttachments.length})`}>
                {/* [Iter 347] Image grid — tap to open in MediaLightbox
                    with the full close / download / prev / next stack. */}
                {imageAttachments.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                    {imageAttachments.map((a, i) => {
                      const src = attachmentSrc(a);
                      if (!src) return null;
                      return (
                        <button
                          key={a.id || i}
                          type="button"
                          onClick={() => setLightboxIndex(i)}
                          className="block aspect-square rounded overflow-hidden border border-white/10 hover:border-[#1FB6C1]/50 relative"
                          data-testid={`portal-ticket-att-image-${i}`}
                        >
                          <img
                            src={src}
                            alt={a.label || a.filename || ""}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">
                            <ImageIcon className="w-2.5 h-2.5 inline mr-1" />
                            {a.label || a.filename || a.name || `Image ${i + 1}`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {docAttachments.length > 0 && (
                  <ul className="space-y-1.5 text-sm">
                    {docAttachments.map((a, i) => (
                      <li key={a.id || i}>
                        <button
                          type="button"
                          onClick={() => downloadPortalAttachment(a)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded border border-white/5 hover:border-[#1FB6C1]/40 transition text-left"
                          data-testid={`portal-ticket-att-${i}`}
                        >
                          <FileText className="w-4 h-4 text-[#1FB6C1]" />
                          <span className="flex-1 truncate">
                            {a.label || a.filename || a.name || "Attachment"}
                          </span>
                          <Download className="w-3.5 h-3.5 text-[#647087]" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            )}

            {/resolved|closed/.test(ticket.status || "") && (
              <Section title="Outcome">
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  <span>
                    This ticket is <strong>{ticket.status.replace("_", " ")}</strong>.
                    Reach out if anything's not behaving as expected and we'll
                    open a fresh visit.
                  </span>
                </div>
              </Section>
            )}

            <div className="border-t border-white/10 pt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-[#647087] italic max-w-[60%]">
                Internal resolution notes, costs and staff-only attachments are
                filtered out of this view automatically.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadPdf()}
                  disabled={downloadingPdf}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="portal-ticket-pdf-bottom"
                >
                  {downloadingPdf ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…</>
                  ) : (
                    <><Download className="w-3.5 h-3.5" /> Download PDF</>
                  )}
                </button>
                {/* [Iter 290zp.32] Removed portal "Print" button — same
                    reason as PortalJobCardDetail: it fell through to
                    `window.print()`, producing a browser web-print
                    (URL + page counter header/footer, no branding).
                    Download PDF now uses WeasyPrint only. */}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* [Iter 347] Image lightbox for ticket photo attachments — keeps
          customers in the portal shell with close / prev / next /
          download / share, matching the JC portal view. */}
      <MediaLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(-1)}
      />
    </div>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="hv-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children, action = null }) {
  return (
    <section className="hv-card p-5">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="font-display text-base font-bold text-white">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
